'use client'

import * as React from 'react'

import { Button, Separator } from '@ui/web/components'
import { buildUrl } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { RenderLink } from '../utils'

/**
 * Represents a navigation link in the tab header.
 */
interface TabLink {
  /** The URL path segment for this tab */
  link: string
  /** The display label for this tab */
  label: string
  /** Whether this tab should be disabled */
  disabled?: boolean
}

/**
 * Optional header configuration for the layout.
 */
interface HeaderConfig {
  /** Custom JSX to render in the header area */
  jsx?: React.ReactNode
  /** Unique identifier for the header */
  id: string
  /** Type classification for the header */
  type: string
}

/**
 * Props for the HeaderTabLayout component.
 */
interface HeaderTabLayoutProps {
  /** Content to render below the tab navigation */
  children: React.ReactNode
  /** Array of tab navigation links */
  links: TabLink[]
  /** Base URL segments used to construct tab URLs */
  baseUrl: string[]
  /** Optional header configuration */
  header?: HeaderConfig
  /** Application type for URL building */
  type: 'web' | 'school' | 'admin'
  /** Optional CSS class for the outer container */
  className?: string
  /** Optional CSS class for the content container */
  containerClassName?: string
  /** Optional CSS class for the navigation container */
  navContainerClassName?: string
  /** Current pathname from the router (framework-agnostic) */
  pathname?: string
  /**
   * Render function for tab links (e.g. from createRenderLink in @ui/web/utils).
   * When not provided, non-disabled tabs render as plain <a>.
   */
  renderLink?: RenderLink
  /** Custom hook or function to get the current pathname */
  usePathnameHook?: () => string
}

/**
 * Extracts the organization ID from the pathname segments.
 *
 * @param pathSegments - Array of pathname segments
 * @returns The organization ID if found, undefined otherwise
 */
function extractOrgId(pathSegments: string[]): string | undefined {
  return pathSegments.find((segment) => segment.includes('org_'))
}

/**
 * Extracts the current path segment from the pathname.
 *
 * @param pathname - The full pathname string
 * @returns The last segment of the pathname
 */
function extractCurrentPathSegment(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean)
  return segments[segments.length - 1]
}

/**
 * Builds the complete URL for a tab link.
 *
 * @param params - URL building parameters
 * @returns The complete URL string
 */
function buildTabUrl(params: {
  type: 'web' | 'school' | 'admin'
  baseUrl: string[]
  link: string
  orgId?: string
  header?: HeaderConfig
}): string {
  const { type, baseUrl, link, orgId, header } = params

  const path = type !== 'admin' ? `/${orgId}/${baseUrl.join('/')}/${link}` : `/${baseUrl.join('/')}/${link}`

  const queryParams = header
    ? {
        type: encodeURIComponent(JSON.stringify([header.type])),
        id: header.id,
      }
    : undefined

  const url = buildUrl(type, path, queryParams)
  return typeof url === 'string' ? url : url.toString()
}

/**
 * HeaderTabLayout - A framework-agnostic tabbed navigation layout component.
 *
 * This component provides a responsive tab navigation interface with customizable styling
 * and framework-agnostic routing support. It can work with Next.js, React Router, or any
 * other routing solution by providing the appropriate props.
 *
 * @example
 * // Next.js: use createRenderLink(Link) from @ui/shared/utils
 * import { createRenderLink } from '@ui/shared/utils'
 * import Link from 'next/link'
 * import { usePathname } from 'next/navigation'
 *
 * const renderLink = createRenderLink(Link)
 *
 * <HeaderTabLayout
 *   links={[{ link: 'overview', label: 'Overview' }, { link: 'settings', label: 'Settings' }]}
 *   baseUrl={['dashboard']}
 *   type="web"
 *   renderLink={renderLink}
 *   usePathnameHook={usePathname}
 * >
 *   <div>Tab content here</div>
 * </HeaderTabLayout>
 *
 * @example
 * // TanStack Router: createRenderLink(Link, 'to')
 * const renderLink = createRenderLink(Link, 'to')
 */
export function HeaderTabLayout({
  children,
  links,
  baseUrl,
  header,
  type,
  className,
  containerClassName,
  navContainerClassName,
  pathname: pathnameFromProps,
  renderLink,
  usePathnameHook,
}: HeaderTabLayoutProps) {
  // Get pathname from props, hook, or fallback to empty string
  const pathnameFromHook = usePathnameHook?.()
  const pathname = pathnameFromProps ?? pathnameFromHook ?? ''

  // Parse pathname to extract relevant segments
  const pathSegments = React.useMemo(() => pathname.split('/').filter(Boolean), [pathname])

  const orgId = React.useMemo(() => extractOrgId(pathSegments), [pathSegments])

  const currentPathSegment = React.useMemo(() => extractCurrentPathSegment(pathname), [pathname])

  const tabButtonClassName = React.useMemo(
    () =>
      cn(
        'ui:opacity-100 ui:cursor-not-allowed ui:hover:no-underline ui:disabled:opacity-100 ui:disabled:cursor-not-allowed ui:disabled:hover:no-underline',
        'ui:p-0',
      ),
    [],
  )

  const tabLinkButtonClassName = React.useMemo(() => cn('ui:opacity-50', 'ui:p-0'), [])

  /**
   * Renders a disabled tab (no link) with button styling.
   */
  const renderDisabledTab = React.useCallback(
    (params: { label: string }) => {
      return (
        <Button disabled variant='link' className={tabButtonClassName}>
          {params.label}
        </Button>
      )
    },
    [tabButtonClassName],
  )

  return (
    <div
      className={cn(
        'ui:bg-neutrals-white ui:rounded-xl ui:shadow-sm ui:flex ui:flex-col ui:gap-y-8 ui:mb-20',
        className,
      )}
    >
      {header?.jsx}

      <div className={cn('ui:flex ui:flex-col ui:gap-y-2', containerClassName)}>
        <nav
          className={cn(
            'ui:flex ui:items-center ui:space-x-4 ui:text-sm ui:text-nowrap ui:w-full ui:max-w-full ui:overflow-x-auto ui:h-fit ui:px-4 ui:pt-4',
            navContainerClassName,
          )}
          role='navigation'
          aria-label='Tab navigation'
        >
          {links.map((tabLink, index) => {
            const isActiveTab = currentPathSegment?.includes(tabLink.link) ?? false
            const isDisabled = isActiveTab || tabLink.disabled === true

            const tabUrl = buildTabUrl({
              type,
              baseUrl,
              link: tabLink.link,
              orgId,
              header,
            })

            return (
              <React.Fragment key={tabLink.link}>
                {isDisabled ? (
                  renderDisabledTab({ label: tabLink.label })
                ) : (
                  <Button asChild variant='link' className={tabLinkButtonClassName}>
                    {renderLink ? (
                      renderLink({ href: tabUrl, children: tabLink.label })
                    ) : (
                      <a href={tabUrl}>{tabLink.label}</a>
                    )}
                  </Button>
                )}

                {index + 1 !== links.length && (
                  <Separator orientation='vertical' className='ui:h-6 ui:data-[orientation=vertical]:self-auto' />
                )}
              </React.Fragment>
            )
          })}
        </nav>

        <Separator />

        <div className='ui:mt-4'>{children}</div>
      </div>
    </div>
  )
}
